<template>
  <div class="prices-container">
    <div class="skin-prices">
      <div
        v-if="item.price_range"
        class="price-range"
      >
        <span class="range-name">
          {{
            item.price_range?.highest_price && locale === 'en'
              ? t('Price range') : t('price')
          }}
        </span>
        <div class="prices">
          <NuxtLink
            :to="getItemLink({ slug: item.price_range.lowest_link! })"
            rel="ugc"
          >
            <LazyUiPrice
              :price="item.price_range.lowest_price"
            />
          </NuxtLink>
          <span v-if="item.price_range.highest_price"> - </span>
          <NuxtLink
            v-if="item.price_range.highest_price"
            :to="{
              path: getItemLink({ slug: item.price_range.highest_link! }),
              ...(item.price_range.highest_link && item.price_range.lowest_link === item.price_range.highest_link ? { query: { offers: 'r' } } : {}),
            }"
            rel="ugc"
          >
            <LazyUiPrice
              :price="item.price_range.highest_price"
            />
          </NuxtLink>
        </div>
      </div>
      <div
        v-if="item.extra_range"
        :class="{
          'souvenir-range': item.has_souvenir,
          'stattrack-range': item.has_stattrak,
        }"
        class="price-range"
      >
        <span class="range-name">{{ item.has_souvenir ? 'Souvenir' : "StatTrak" }}</span>
        <div class="prices">
          <NuxtLink :to="getItemLink({ slug: item.extra_range.lowest_link! })">
            <LazyUiPrice
              :price="item.extra_range.lowest_price"
            />
          </NuxtLink>
          <span v-if="item.extra_range.highest_price">-</span>
          <NuxtLink
            v-if="item.extra_range.highest_price"
            :to="getItemLink({ slug: item.extra_range.highest_link! })"
          >
            <LazyUiPrice
              :price="item.extra_range.highest_price"
            />
          </NuxtLink>
        </div>
      </div>
      <div
        v-if="!item.price_range && !item.extra_range"
        class="price-range"
      >
        <span class="range-name">
          {{ t("No price information") }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ItemsByField } from '~/graphql/generated/schema-types';
import { useI18n } from 'vue-i18n';

defineProps<{
  item: ItemsByField
}>();

const { t, locale } = useI18n();
</script>

<style lang="scss" scoped>
.prices-container {
  margin: 0.8rem 0;
  position: relative;
  z-index: 2;

  .skin-prices {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    justify-content: center;
    margin-left: 0.8rem;
    margin-right: 0.8rem;

    .price-range {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      gap: 8px;
      flex-wrap: wrap;
      color: var(--gray);

      .range-name {
        white-space: nowrap;
        font-weight: 400;
        font-size: 1.4rem;
        line-height: 1.9rem;
      }

      .prices {
        display: flex;

        &:deep(p),
        &:deep(span),
        &:deep(.price) {
          font-weight: 800;
          font-size: 1.6rem;
          line-height: 2.2rem;
          color: var(--surfaces-on-surface);

          &:hover {
            text-decoration: underline;
          }

          span[class^="c-"] {
            padding: 4px 6px;
          }
        }
      }

      &.stattrack-range {
        .range-name {
          color: var(--stattrak);
        }

        .prices {
          color: var(--stattrak);

          &:deep(p),
          &:deep(span),
          &:deep(.price) {
            color: var(--stattrak);

          }
        }
      }

      &.souvenir-range {
        .range-name {
          color: var(--souvenir);
        }

        .prices {
          color: var(--souvenir);

          &:deep(p),
          &:deep(span),
          &:deep(.price) {
            color: var(--souvenir);

          }
        }
      }
    }
  }
}
</style>
